<template>
  <v-row align="center" justify="center">
    <v-col cols="12">
      <v-card elevation="0" tile>
        <v-card-title class="grey lighten-4">
          {{ $t("hr.menu.imprest_approval") }}
          <v-spacer />
          <id-button @click="onRefreshData">
            <template #icon>
              <v-icon left>mdi-refresh</v-icon>
            </template>
            {{ $t("buttons.refresh") }}
          </id-button>
        </v-card-title>
        <v-data-table
          :headers="table.headers"
          :items="table.data"
          :options.sync="table.options"
          :server-items-length="table.totalItems"
          single-select
          class="elevation-1"
          :loading="table.loading"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        >
          <template slot="item" slot-scope="row">
            <tr :key="row.item.id">
              <td class="text-xs-left no-wrap">
                <employee-profile :employee="row.item.imprest.employee" />
              </td>
              <td class="text-xs-left no-wrap">
                {{ $helpers.getConvertCurrency(row.item.imprest.requested) }}
              </td>
              <td class="text-xs-left no-wrap">
                {{ row.item.imprest.period | formattedDate }}
              </td>
              <td>
                <template v-if="row.item.imprest.travel">
                  <p class="ma-0 caption">
                    <v-icon size="20" color="teal">mdi-account</v-icon>
                    {{ row.item.imprest.travel.employee.firstName }}
                    {{ row.item.imprest.travel.employee.lastName }}
                  </p>
                  <p class="ma-0 caption">
                    <v-icon size="20" color="teal">mdi-map-marker</v-icon>{{ row.item.imprest.travel.address }}
                  </p>
                  <p class="ma-0 caption">
                    <v-icon size="20" color="teal">mdi-calendar-range</v-icon>
                    {{ row.item.imprest.travel.beginDate | formattedDateTimeMinute }} -
                    {{ row.item.imprest.travel.endDate | formattedDateTimeMinute }}
                  </p>
                </template>
                <v-icon v-else color="red">mdi-close</v-icon>
              </td>
              <td>
                <template v-if="row.item.imprest.expenseManagement">
                  <p class="ma-0">
                    {{ row.item.imprest.expenseManagement.name }}
                  </p>
                  <p v-if="row.item.imprest.expenseManagement.project" class="ma-0 caption">
                    <v-avatar size="14" :color="row.item.imprest.expenseManagement.project.color" />
                    {{ row.item.imprest.expenseManagement.project.name }}
                  </p>
                </template>
                <v-icon v-else color="red">mdi-close</v-icon>
              </td>
              <td class="text-xs-left no-wrap">
                <request-status-label :status="row.item.imprest.requestStatus" />
              </td>
              <td class="text-right">
                <v-btn small depressed color="primary" dark @click.stop="onClickExpenseApproval(row.item)">
                  {{ $t("buttons.action") }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <imprest-approval-dialog
      v-model="dialog.status"
      :item.sync="dialog.data"
      @close="onCloseDialog"
      @success="onSuccessApproval"
    />
  </v-row>
</template>

<script>
  import * as GQL from "./query";
  import RequestStatusLabel from "../../../../components/global/request-status-label";

  export default {
    name: "ImprestApproval",
    components: {
      RequestStatusLabel,
      "imprest-approval-dialog": () => import("./components/confirm-dialog")
    },
    data: vm => ({
      dialog: {
        status: false,
        data: null
      },
      table: {
        loading: false,
        data: [],
        criteria: {
          employees: [],
          beginDate: null,
          endDate: null
        },
        options: {},
        pagination: {
          rowsPerPage: 50,
          sortBy: "imprest.timestamp",
          descending: true
        },
        totalItems: 0,
        totalPages: 0,
        selected: [],
        headers: [
          {
            text: vm.$t("hr.imprest_approval.employee"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.imprest_approval.amount"),
            align: "left",
            sortable: false,
            value: "amount",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.imprest_approval.request_date"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: vm.$t("hr.expense.travel"),
            value: "",
            align: "left",
            sortable: false,
            class: ["primary--text"]
          },
          {
            text: vm.$t("global.expense_management"),
            value: "",
            align: "left",
            sortable: false,
            class: ["primary--text"]
          },
          {
            text: vm.$t("hr.imprest_approval.status"),
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-lg-left"]
          },
          {
            text: "",
            align: "left",
            sortable: false,
            value: "",
            class: ["primary--text text-right"]
          }
        ]
      }
    }),
    watch: {
      "table.options": {
        handler(options) {
          let { itemsPerPage, page } = options;
          this.table.pagination.rowsPerPage = itemsPerPage;
          this.table.pagination.page = page;
          this.fetchItems();
        },
        deep: true
      }
    },
    methods: {
      onRefreshData() {
        this.fetchItems();
      },
      async fetchItems() {
        this.table.loading = true;
        await this.$apollo
          .query({
            query: GQL.MANAGER_APPROVER_IMPREST,
            variables: {
              id: this.$store.state.auth.user.id
            },
            fetchPolicy: "no-cache",
            errorPolicy: "all"
          })
          .then(({ data, errors }) => {
            if (!errors || !data.error) {
              this.table.data = data.employee.pendingApprovalImprestRequests || [];
              this.table.totalItems = data.employee.pendingApprovalImprestRequests.length || 0;
              this.table.totalPages = data.employee.pendingApprovalImprestRequests.length || 0;
            }
          })
          .catch(e => {
            this.$helpers.showNotification(e.message);
          })
          .finally(() => {
            this.table.loading = false;
          });
      },
      onClickExpenseApproval(imprest) {
        this.dialog.data = imprest;
        this.dialog.status = true;
      },
      onCloseDialog() {
        this.dialog.data = null;
        this.dialog.status = false;
      },
      onSuccessApproval() {
        this.onCloseDialog();
        this.onRefreshData();
      }
    }
  };
</script>

<style scoped></style>
